import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { HAND_TYPE } from 'constants/gameConstants';
import { useAnimationPreferences } from 'contexts/AnimationPreferencesContext';
import gsap from 'gsap';
import { useOrientation } from 'hooks/useOrientation';
import { useTableSelector } from 'hooks/useTableSelector';
import { useTableSnap } from 'hooks/useTableSnap';
import { tableMoneyFormatterCompact, toDecimal } from 'utils/functions';

import PokerChip from 'components/Table/PokerChip';

type CashPropType = {
  cash: number;
  player: string;
  playerGUID: string;
  isCharged: boolean;
  seatId: number;
};

export const Cash = ({ cash, player, playerGUID, seatId }: CashPropType) => {
  const { reduceAnimations } = useAnimationPreferences();
  const {
    tableOverBet,
    tabletype: tableType,
    pots,
    showdown: { currentPot, currentStep, handType }
  } = useTableSelector((table) => table.game);
  const shouldSnap = useTableSnap();
  const [cashAmount, setCashAmount] = useState<number>(0);
  const [totalCashAmount, setTotalCashAmount] = useState<number>(0);
  const tableCharges = useTableSelector((table) => table.tableCharges);
  const proxyRef = useRef({ value: 0 });
  const tweenRef = useRef<gsap.core.Tween | null>(null);

  let multiplier = 0;
  if (tableCharges[playerGUID] !== undefined) {
    multiplier = tableCharges[playerGUID]?.multiplier;
    player += ` - C${multiplier}`;
  }

  useEffect(() => {
    let total = 0;

    for (let i = 0; i <= currentPot; i++) {
      const pot = pots[i];
      const winners = pot?.winners?.filter((f: any) => +f.player === +seatId);

      if (winners?.length > 0) {
        total += winners
          .filter(
            (f: any) =>
              i < +currentPot ||
              (i === +currentPot &&
                ((f.handType === HAND_TYPE.HI && handType === HAND_TYPE.LO) ||
                  (f.handType === handType && +currentStep > 2) ||
                  (!f.handType && +currentStep > 2)))
          )
          .map((f: any) => +f.win)
          .reduce((t: number, num: number) => t + num, 0);
      }
    }

    if (tableOverBet.seatId === +seatId) {
      total += +tableOverBet?.bet ?? 0;
    }

    setTotalCashAmount(cash + total);
  }, [cash, seatId, currentPot, currentStep, tableOverBet.bet]);

  const stackRef = useRef<HTMLDivElement>(null);
  const prevCashRef = useRef(totalCashAmount);

  // Animated number tick + win celebration
  useEffect(() => {
    if (tweenRef.current) tweenRef.current.kill();
    const stackEl = stackRef.current;

    const isWinning = totalCashAmount > prevCashRef.current && prevCashRef.current > 0;
    const increase = totalCashAmount - prevCashRef.current;
    prevCashRef.current = totalCashAmount;

    // Snapping (table switch / resync): every stack at the table is already at
    // its current size — show the figures, don't roll every seat's counter up
    // to them (a per-seat tween burst in exactly the frames a switch needs).
    if (shouldSnap() || reduceAnimations) {
      if (stackEl && reduceAnimations) gsap.set(stackEl, { scale: 1, color: '#a8d88e' });
      proxyRef.current.value = totalCashAmount;
      setCashAmount(tableType !== 's' ? +toDecimal(totalCashAmount) : +totalCashAmount.toFixed(2));
      return;
    }

    // Slow dramatic count-up on win, fast on normal changes
    const duration = isWinning ? Math.min(1.5, 0.5 + increase / 500) : 0.5;

    tweenRef.current = gsap.to(proxyRef.current, {
      value: totalCashAmount,
      duration,
      ease: isWinning ? 'power2.out' : 'none',
      snap: { value: 0.01 },
      onUpdate: () => {
        let bet = proxyRef.current.value.toFixed(2);
        if (tableType !== 's') bet = String(toDecimal(proxyRef.current.value));
        setCashAmount(+bet);
      }
    });

    // Win celebration: scale bounce + green flash
    if (isWinning && stackRef.current) {
      gsap.fromTo(
        stackRef.current,
        { scale: 1, color: '#a8d88e' },
        {
          scale: 1.3,
          duration: 0.3,
          ease: 'back.out(2)',
          yoyo: true,
          repeat: 1,
          onComplete: () => {
            gsap.to(stackRef.current, { scale: 1, duration: 0.2 });
          }
        }
      );
      // Flash the text gold then back
      gsap.fromTo(
        stackRef.current,
        { color: '#FFD700' },
        { color: '#a8d88e', duration: 1.5, delay: 0.5, ease: 'power2.out' }
      );
    }

    return () => {
      if (tweenRef.current) tweenRef.current.kill();
      // Kill the untracked celebration fromTo/gold-flash tweens too, so a seat
      // that unmounts mid-celebration doesn't orphan them against a detached node.
      if (stackEl) gsap.killTweensOf(stackEl);
    };
    // shouldSnap is read inside the effect on purpose — see hooks/useTableSnap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCashAmount, tableType, reduceAnimations]);

  const formattedAmount = tableMoneyFormatterCompact(cashAmount, tableType);

  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const counterScale = isPortrait ? 1 / 0.7 : 1;

  return (
    <div
      style={{
        ...containerStyle,
        transform: isPortrait ? `scale(${counterScale})` : undefined,
        transformOrigin: 'top center'
      }}>
      <div style={nameStyle}>{player}</div>
      <div ref={stackRef} style={stackRowStyle}>
        <PokerChip size={14} color='green' />
        <span style={amountStyle}>{formattedAmount}</span>
      </div>
    </div>
  );
};

const containerStyle: CSSProperties = {
  position: 'absolute',
  top: 78,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  gap: '2px'
};

const nameStyle: CSSProperties = {
  whiteSpace: 'nowrap',
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.85)',
  textShadow: '0 1px 3px rgba(0,0,0,0.5)',
  maxWidth: '100px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textAlign: 'center'
};

const stackRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '3px',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  borderRadius: '10px',
  padding: '2px 8px 2px 4px',
  border: '1px solid rgba(255, 255, 255, 0.08)'
};

const amountStyle: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  color: '#e8e8e8',
  whiteSpace: 'nowrap'
};
